import { Component } from "../base/Component";
import { ComponentFactory } from "../../service/ComponentFactory";
import { EventUtil } from "../../util/EventUtil";
import { ILandingPageLoadRequest } from "../../models/LandingPage/ILandingPageLoadRequest";
import { PageContext } from "../../service/PageContext";
import { SpecialtySelect } from "../healioGlobal/SpecialtySelect";
import { SubSpecialtySelect } from "../healioGlobal/SubSpecialtySelect";
import { TopicFilter } from "../topicFilter/TopicFilter";
import { LandingCardLoader } from "./LandingCardLoader";
import { WyanokeUtil } from "../../util/WyanokeUtil";
import { moduletracking } from "../../Wyanoke/ModuleTracking";
import * as Mustache from "mustache";
import { UiUtil } from "../../util/UiUtil";
import { TopicAndCreditTypeFilter } from "../topicAndCreditTypeFilter/TopicAndCreditTypeFilter";
import { ViewBySpecialty } from "../news/ViewBySpecialty";
import { fetchSavedForSession, parseSavedMeta } from "../../service/HealioWidgetsSavedApi";

/**
 * Card services that list the signed-in user's saved items, and the saved
 * targetType each one lists. Their ids come from healionetwork-be (through
 * /healiowidgets) and are sent to the service as `savedIds=` in
 * additionalParameters; the service only renders those items' cards.
 * An element can override the type with data-saved-target-type.
 */
const SAVED_LISTING_SERVICES: { [serviceName: string]: string } = {
  mysavedlearningsites: "learntheheart",
};

export interface ILandingCardLayoutLoadedDetail {
  readonly current: number;
  readonly total: number;
  readonly featured: string;
  readonly isEmpty?: boolean;
}

export class LandingCardLayout extends Component {
  public static readonly LandingCardLayoutLoadedEvent =
    "LandingCardLayoutLoaded";
  public static readonly LandingCardLayoutLoadedInit =
    "LandingCardLayoutInit";

  private readonly _articleItemTemplate:
    string | null = null;
  private readonly _featuredItemTemplate:
    string | null = null;
  private _lastKnownScroll: number | null;
  private _lastRequestId: string | null = null;
  private readonly _listItemTemplate:
    string | null = null;
  private _startPage: number = 1;
  private _loading: boolean = false;
  private _spinner: Element | null;
  private _tempDivElement: JQuery<HTMLElement> = $("<div></div>");

  constructor(element: HTMLElement, context: PageContext) {
    super(element, context);

    this._lastKnownScroll = 0;
    this._spinner = document.querySelector(".cme-spinner");

    const featuredContentElement = element.closest(".row")?.querySelector(".featured-content");
    if(featuredContentElement !== undefined && featuredContentElement !== null){
      element.classList.add("featured-col");
    }

    const listItemTemplateElement = $("#ListingItemTemplate");
    if (listItemTemplateElement.length) {
      const listingItemTemplateHtml = listItemTemplateElement.html().trim();
      this._tempDivElement.html(listingItemTemplateHtml);
      // tslint:disable-next-line: max-line-length
      if (this._tempDivElement.find('[data-card-element="article-template"]').length) {
        this._articleItemTemplate = this._tempDivElement.find('[data-card-element="article-template"]').html();
        // tslint:disable-next-line: max-line-length
        this._tempDivElement.find('[data-card-element="article-template"]').html("");
      }
      this._listItemTemplate = this._tempDivElement.html();
    }

    const featuredItemTemplateElement = $("#FeaturedItemTemplate");
    if (featuredItemTemplateElement.length) {
      this._featuredItemTemplate = featuredItemTemplateElement.html().trim()
    }

    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get("page");
    if (page !== undefined && page !== null) {
      this._startPage = Number.parseInt(page);
    }

    document.addEventListener(TopicFilter.TopicFilterChangeEvent, () =>
      this.handleFilterChange()
    );

    document.addEventListener(
      SubSpecialtySelect.SubSpecialtySelectInitEvent, () =>
      this.handleSubSpecLoad()
    );

    document.addEventListener(LandingCardLoader.LoadPageEvent, ev =>
      this.handleLoadPage(ev as CustomEvent<number>)
    );

    document.addEventListener(LandingCardLoader.LoadFilteredPageEvent, ev =>
      this.changeFilter(ev as CustomEvent<string>)
    );

    document.addEventListener(LandingCardLoader.LoadMoreEvent, ev =>
      this.handleLoadMore(ev as CustomEvent<number>)
    );

    window.onerror = () => {
      this.stopSpinner(0);
    }

    document.onerror = () => {
      this.stopSpinner(0);
    }
    $(".card--layout").masonry({
      columnWidth: ".card-masonry",
      gutter: ".card-gutter",
      itemSelector: ".card-masonry",
      percentPosition: true
    });

    this._element.dispatchEvent(
      EventUtil.createCustomEvent(
        LandingCardLayout.LandingCardLayoutLoadedInit,
        ""
      )
    );
  }

  public async init(): Promise<void> {
    if (this._element.dataset.autoload === "True") {
      await this.loadCards(this._startPage, true);
    }
  }

  private scrollTop(): void {
    $("html, body").animate(
      {
        scrollTop: 0
      },
      "slow"
    );
  }

  private stopSpinner(timeout: number): void {
    setTimeout(() => {
      if (this._spinner) {
        this._spinner.classList.remove("active");
      }
    }, timeout);
  }

  private startSpinner(): void {
    if (this._spinner) {
      this._spinner.classList.add("active");
    }
  }
  private async changeFilter(ev: CustomEvent<string>): Promise<void> {
    this._element.dataset.additionalParameters = ev.detail;
    await this.handleFilterChange();
  }

  private async handleFilterChange(): Promise<void> {
    return this.loadCards(this._startPage, true);
  }
  private async handleSubSpecLoad(): Promise<void> {
    return this.loadCards(this._startPage, true);
  }

  private async handleLoadMore(ev: CustomEvent<number>): Promise<void> {
    return this.loadCards(ev.detail, false);
  }

  private async handleLoadPage(ev: CustomEvent<number>): Promise<void> {
    await this.loadCards(ev.detail, true);

    return this.scrollTop();
  }

  private renderCardsFromTemplate(cards: any): string {
    let renderedCards = "";
    // tslint:disable-next-line: no-unsafe-any
    if (this._listItemTemplate) {
      for(let card of cards) {
        const renderedTemplate = Mustache.render(this._listItemTemplate,card);
        this._tempDivElement.html(renderedTemplate);
        if (card.Id === "") {
          this._tempDivElement.find('[data-card-element="content"]').remove();
        } else {
          this._tempDivElement.find('[data-card-element="ad"]').remove();
          if (this._articleItemTemplate && card.Articles) {
            // tslint:disable-next-line: no-unsafe-any
            for(let article of card.Articles){
                const renderedArticle = Mustache.render(this._articleItemTemplate,article);
                this._tempDivElement.find('[data-card-element="article-template"]').append(renderedArticle);
              }

          }
        }
        this._tempDivElement.find('[data-card-element="content"]')
          .children().unwrap();
        renderedCards += UiUtil.replaceDataSrcWithSrcInImageTags(this._tempDivElement.html());
      };
    }

    return renderedCards;
  }

  // tslint:disable-next-line: cyclomatic-complexity
  private async loadCards(page: number, replace: boolean): Promise<void> {
    if (this._loading) {
      return;
    }
    try {
      this._loading = true;
      this._lastKnownScroll = window.scrollY;
      const context = this._pageContext;
      if (!context) {
        throw new Error("No page context");
      }
      // Do we use userid in this component or in the service calls?
      let userId = "00000000-0000-0000-0000-000000000000";
      if (this._element.dataset.requireUserprofile === "True") {
        // we await user profile
        const profile = await WyanokeUtil.loadUserProfile(true, false);
        if (!profile) {
          console.error("Profile is not loaded");

          return;
        }
        userId = (profile.contactid || userId);
      }

      const serviceName = this._element.dataset.serviceName
        ? this._element.dataset.serviceName
        : "";

      const service = context.getCardService(serviceName);
      if (!service) {
        throw new Error("No service found for key: " + serviceName);
      }
      this.startSpinner();
      console.log("loaded service: " + serviceName);

      const specialtySelect: SpecialtySelect | null =
        context.componentFactory.getComponent(SpecialtySelect);

      const topicFilter: TopicFilter | null =
        context.componentFactory.getComponent(TopicFilter);

      const creditTypeFilter: TopicAndCreditTypeFilter | null =
        context.componentFactory.getComponent(TopicAndCreditTypeFilter);

      const subspecialtySelect: SubSpecialtySelect | null =
        context.componentFactory.getComponent(SubSpecialtySelect);

      const templates = this._element.dataset.templateIds
        ? this._element.dataset.templateIds
        : "";

      let specialty = specialtySelect
        ? specialtySelect.specialty
        : null;

      // sometimes we will use a 'view by specialty' component instead of the regular specialty select control
      if(!specialty) {
        const viewBySpecialty: ViewBySpecialty | null = context.componentFactory.getComponent(ViewBySpecialty);
        specialty = viewBySpecialty ? viewBySpecialty.specialty : null;
      }

      let topics;

      if(topicFilter){
        topics = topicFilter
          ? topicFilter.topics.map(topic => topic.topicId)
          : null;
      } else if(creditTypeFilter){
        topics = creditTypeFilter
          ? creditTypeFilter.topics.map(topic => topic.topicId)
          : null;
      } else {
        topics = null;
      }

      const creditTypes = creditTypeFilter
        ? creditTypeFilter.creditTypes.map(creditType => creditType.creditTypeId)
        : null;

      const subspecialty = subspecialtySelect
        ? subspecialtySelect.subspecialty
        : null;

      const requestId =
        Math.random()
          .toString(36)
          // tslint:disable-next-line: no-magic-numbers
          .substring(2, 15) +
        Math.random()
          .toString(36)
          // tslint:disable-next-line: no-magic-numbers
          .substring(2, 15);
      this._lastRequestId = requestId;

      let additionalParameters = this._element.dataset.additionalParameters
        ? this._element.dataset.additionalParameters
        : null;

      const savedTargetType =
        this._element.dataset.savedTargetType ||
        SAVED_LISTING_SERVICES[serviceName.toLowerCase().replace(/serviceload$/, "")];
      if (savedTargetType) {
        const savedRows = await fetchSavedForSession({ targetType: savedTargetType });
        // Skip saves made on another site (e.g. a .master preview); a save with no site is kept.
        const siteKey = (this._element.dataset.siteName || "").toLowerCase();
        const siteRows = savedRows.filter(row => {
          const rowSite = (parseSavedMeta(row.data ?? undefined).site || "").toLowerCase();
          return !rowSite || !siteKey || rowSite === siteKey;
        });
        const savedIds = "savedIds=" + siteRows.map(row => row.targetId).join(",");
        additionalParameters = additionalParameters
          ? additionalParameters + "&" + savedIds
          : savedIds;
      }

      const pageId = this._element.dataset.pageId
        ? this._element.dataset.pageId
        : null;

      const request: ILandingPageLoadRequest = {
        additionalParameters,
        page,
        pageId,
        requestId,
        templates,
        specialty,
        subspecialty,
        topics,
        userId,
        creditTypes
      };

      const loadPromise = service.load(request);

      const response = await loadPromise;

      if (!this._lastRequestId ||
        !response.requestId ||
        response.requestId !== this._lastRequestId) {
        return;
      }

      const divNode = document.createElement("div")
      divNode.classList.add("card-gutter");

      // tslint:disable-next-line: no-unsafe-any
      let cards = response.cards;
      // tslint:disable-next-line: no-unsafe-any
      let featured = response.featured;

      if (this._listItemTemplate && typeof (response.cards) === "object") {
        // tslint:disable-next-line: no-unsafe-any
        cards = this.renderCardsFromTemplate(response.cards);

        // tslint:disable-next-line: no-unsafe-any
        if (featured !== undefined && featured.Id !== undefined) {
          const renderedFeaturedTemplate = this._featuredItemTemplate ?
            Mustache.render(this._featuredItemTemplate,featured) : "";
          this._tempDivElement.html(renderedFeaturedTemplate);
          this._tempDivElement.find('[data-card-element="ad"]').remove();
          featured = UiUtil.replaceDataSrcWithSrcInImageTags(this._tempDivElement.html());
        }
      }

      if (replace) {
        let child = this._element.firstElementChild;
        while (child) {
          context.componentFactory.dereferenceComponents(child as HTMLElement);
          child.remove();
          child = this._element.firstElementChild;
        }

        this._element.innerHTML = cards;
        this._element.appendChild(divNode);
        const elements = this._element.querySelectorAll<HTMLElement>(
          ".card-masonry"
        );

        if (elements) {
          elements.forEach(ele => $(ele).hide());
        }
      } else {
        if (page === this._startPage) {
          this._element.appendChild(divNode);
        }
        const frag = document.createDocumentFragment();
        const node = frag.appendChild(document.createElement("div"));
        node.innerHTML = cards;
        while (node.firstChild) {
          $(node.firstChild).hide();
          var id = String($(node.firstChild).data("itemId"));
          var count = $("[data-item-id='" + id + "']").length;
          console.log(id + ": " + count);
          if (id.length === 0 || count === 0) {
            this._element.appendChild(node.firstChild);
          } else {
            node.firstChild.remove();
            console.log("Skipped: " + id);
            console.log(id.length);
          }
        }
      }

      await context.componentFactory.loadComponents(this._element);

      const knownSpecialty = Boolean(specialty);
      const action = knownSpecialty ? "add" : "remove";
      this._element.classList[action]("known-specialty");

      const timeout = 10;
      const spinnerWait = 60;
      // tslint:disable-next-line: typedef
      $(".card-masonry:hidden").each(function () {
        const item = $(this);
        setTimeout(() => {
          item.show();
          $(".card--layout").masonry("appended", item);
          $(".card--layout").masonry("layout");
        }, timeout);
      });

      this.stopSpinner(spinnerWait);

      // Check if we have no cards on the first page after all rendering and filtering
      const actualCardElements = this._element.querySelectorAll(".card-masonry");
      const isEmpty = page === 1 && actualCardElements.length === 0;

      const detail: ILandingCardLayoutLoadedDetail = {
        current: response.current,
        featured,
        total: response.total,
        isEmpty
      };

      if (this._lastKnownScroll && !WyanokeUtil.isMobile()) {
        window.scrollTo(0, this._lastKnownScroll);
      }

      this._element.dispatchEvent(
        EventUtil.createCustomEvent(
          LandingCardLayout.LandingCardLayoutLoadedEvent,
          detail
        )
      );

      moduletracking.AjaxTrackLinks(this._element, "Cards");
      this._loading = false;
    }
    catch(error) {
      console.error(error)
      this.stopSpinner(0);
    }
  }
}

ComponentFactory.registerComponent("LandingCardLayout", LandingCardLayout);
